import { html } from '../../mixins/katapult-element.js';
import { LazySet } from '../../modules/LazySet.js';
import { KatapultEditorTemplate } from './katapult-editor-template.js';
import { Path } from '../../modules/Path.js';
import { ToArray } from '../../modules/ToArray.js';
import '../katapult-elements/katapult-button.js';
import { CamelCase } from '../../modules/CamelCase.js';

class KatapultPhotoToolsetEditor extends KatapultEditorTemplate {
  static get template() {
    return html`
      ${super.style}
      <style>
        .shortcut {
          max-width: 100px;
          justify-content: center;
          text-align: center;
        }
      </style>

      <katapult-firebase-worker id="toolsFirebaseWorker" path="[[modelSpaceDetails.path]]/models/routines" data="{{tools}}">
      </katapult-firebase-worker>

      <template is="dom-repeat" items="[[steps]]" as="step">
        <katapult-editor-step
          creating-item="[[creatingItem]]"
          active="[[step.active]]"
          index="[[step.index]]"
          current-index="[[currentIndex]]"
          title="[[step.title]]"
        >
          <template is="dom-if" if="[[equal(step.id, 'tools')]]" restamp>
            <katapult-button on-click="nextStep" slot="current-step-header">Continue</katapult-button>
            <div style="display: flex; flex-direction: column; flex-grow: 1;" slot="body">
              <paper-table style="max-width: 400px; margin-bottom: 24px;" light-grey rounded outline>
                <paper-row input>
                  <paper-cell>
                    <katapult-drop-down
                      items="[[tools]]"
                      label-function="[[getRoutineLabel]]"
                      value-path="$key"
                      label="Select a Photo Routine"
                      no-label-float
                      on-selected-changed="toolSelectedChanged"
                    >
                      <paper-item on-click="createNewTool">CREATE NEW PHOTO ROUTINE</paper-item>
                    </katapult-drop-down>
                  </paper-cell>
                </paper-row>
              </paper-table>
              <paper-table style="overflow: hidden; flex-grow: 1;" rounded outline white>
                <paper-row grey divider>
                  <paper-cell icon></paper-cell>
                  <paper-cell icon></paper-cell>
                  <paper-cell>Tool</paper-cell>
                  <paper-cell class="shortcut">Shortcut</paper-cell> <paper-cell></paper-cell>
                  <paper-cell icon></paper-cell>
                </paper-row>
                <paper-table-scroll disabled scroll-target="[[$this]]" on-sort-changed="toolsSortChanged">
                  <template is="dom-repeat" items="{{toolsArray}}" as="item">
                    <paper-row white divider slot="sortable">
                      <!-- <paper-cell drag-handle icon>
                        <iron-icon icon="katapult-misc:drag-indicator"></iron-icon>
                      </paper-cell> -->
                      <paper-cell icon>
                        <iron-icon
                          style$="color: [[getToolProperty(item.$key, 'color', tools.*)]];"
                          icon="[[getToolProperty(item.$key, 'icon', tools.*)]]"
                        ></iron-icon>
                      </paper-cell>
                      <paper-cell>[[item.$label]]</paper-cell>
                      <paper-cell class="shortcut">
                        <paper-input label="Shortcut" value="{{item._shortcut}}" maxlength="1" no-label-float>
                          <span slot="prefix">(</span>
                          <span slot="suffix">)</span>
                        </paper-input>
                      </paper-cell>
                      <paper-cell></paper-cell>
                      <paper-cell icon-button>
                        <katapult-button iconOnly noBorder icon="delete" on-click="removeTool"></katapult-button>
                      </paper-cell>
                    </paper-row>
                  </template>
                </paper-table-scroll>
              </paper-table>
            </div>
          </template>
        </katapult-editor-step>
      </template>
      ${super.createItemContainer}
    `;
  }

  static get properties() {
    return {
      backup: {
        type: Object,
        value: {}
      },
      toolAction: {
        type: String
      }
    };
  }

  static get observers() {
    return ['toolsArrayChanged(toolsArray.*)'];
  }

  ready() {
    super.ready();

    this.keyMatchLabel = true;
    this.allowUpperCaseKeys = true;
    this.lastModifiedPath = '_last_modified';

    // An ordered list of all possible steps.
    this.setSteps([
      {
        id: 'tools',
        title: 'Photo Routines'
      }
    ]);
  }

  addTool(key) {
    if (this.get(`_itemData.${key}`) == null) {
      const label = this.tools[key]?.name ?? CamelCase(key);
      this.push('toolsArray', { $key: key, _exists: true, $label: label });
    }
  }

  createNewTool(e) {
    e.currentTarget.parentElement.close();
    document.dispatchEvent(new CustomEvent('create-new-photo-tool'));
  }

  getItemDataToolKeys() {
    if (this._itemData != null) return Object.keys(this._itemData).filter((x) => this.toolFilter(x));
    return [];
  }

  getToolProperty(key, prop) {
    return this.get(`tools.${key}.${prop}`);
  }

  getRoutineLabel(routine) {
    return routine?.name ?? CamelCase(routine?.$key);
  }

  async _saveUpdate(itemData, update, detail) {
    // Init update.
    update = update || {};

    for (let key in this.tools) {
      // Update any tools that are no longer in this toolset.
      if (Path.get(this.tools[key], `toolsets.${detail.itemKey}`) != null && Path.get(itemData, key) == null) {
        update[Path.join([this.$.toolsFirebaseWorker.path, `${key}/toolsets/${detail.itemKey}`], '/')] = null;
      }
      // If the key has changed, remove all instances of the old key.
      if (detail.keyChanged) {
        if (Path.get(this.tools[key], `toolsets.${detail.previousKey}`) != null) {
          update[Path.join([this.$.toolsFirebaseWorker.path, `${key}/toolsets/${detail.previousKey}`], '/')] = null;
        }
      }
    }
    // Update any tools that have been added to this toolset.
    this.getItemDataToolKeys().forEach((key) => {
      if (this.tools?.[key] && Path.get(this.tools[key], `toolsets.${detail.itemKey}`) == null) {
        update[Path.join([this.$.toolsFirebaseWorker.path, `${key}/toolsets/${detail.itemKey}`], '/')] = true;
      }
    });

    await super._saveUpdate(itemData, update, detail);
  }

  toolsArrayChanged(changeRecord) {
    let lazy = new LazySet(this);
    let pathParts = changeRecord.path.split('.');
    // Handle array items changing.
    let itemIndex = Number(pathParts[1]);
    if (!isNaN(itemIndex)) {
      let tool = this.get(`toolsArray.${itemIndex}`);
      let temp = Path.copy(tool);
      delete temp.$key;
      lazy.set(`_itemData.${tool.$key}`, temp);
    }
    // Handle splices.
    else if (pathParts[1] == 'splices') {
      this.toolsArray.forEach((tool, i) => {
        // // Update the tool.
        if (this.get(`_itemData.${tool.$key}`) == null) {
          // tool.priority = i;
          let temp = Path.copy(tool);
          delete temp.$key;
          lazy.set(`_itemData.${tool.$key}`, tool);
        } else {
          // lazy.set(`_itemData.${tool.$key}.priority`, i);
        }
      });
      this.getItemDataToolKeys().forEach((key) => {
        if (this.toolsArray.find((tool) => tool.$key == key) == null) lazy.set(`_itemData.${key}`, null);
      });
    }
    // Cleanup.
    this.getItemDataToolKeys().forEach((key) => {
      let tool = this.get(`_itemData.${key}`);
      if (tool && tool._shortcut == '') lazy.set(`_itemData.${key}._shortcut`, null);
    });
    lazy.notify();
  }

  toolFilter(key) {
    return key != null && ['$', '_'].every((char) => !key.startsWith(char)) && this.get(`_itemData.${key}`) != null;
  }

  toolSelectedChanged(e) {
    let key = e.currentTarget.value;
    this.addTool(key);
    e.currentTarget.value = null;
  }

  toolsSortChanged(e) {
    let removedItem = this.toolsArray.splice(e.detail.prevIndex, 1)[0];
    this.toolsArray.splice(e.detail.index, 0, removedItem);
    this.notifySplices(`toolsArray`, [
      { index: e.detail.prevIndex, removed: [removedItem], addedCount: 0 },
      { index: e.detail.index, removed: [], addedCount: 1 }
    ]);
  }

  removeTool(e) {
    this.splice('toolsArray', e.model.index, 1);
  }

  _itemDataChanged(changeRecord) {
    super._itemDataChanged.apply(this, arguments);
    // Only update toolsArray when the entire _itemData object is changed.
    if (changeRecord.path == '_itemData') {
      const _toolsArray = Object.entries(Path.copy(this._itemData || {})).map(([key, value]) => {
        const label = this.tools[key]?.name ?? CamelCase(key);
        return { $key: key, $label: label, ...value };
      });
      this.toolsArray = _toolsArray.filter((x) => this.toolFilter(x.$key)).sort((a, b) => a.priority - b.priority);
    }
  }
}

customElements.define('katapult-photo-toolset-editor', KatapultPhotoToolsetEditor);
